import React, { createContext, useState, useContext, ReactNode } from 'react';

type Language = 'zh' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

// 创建语言上下文
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// 翻译数据
const translations = {
  zh: {
    // 导航和标题
    'app.title': 'AI学习分析报告',
    'app.subtitle': '智能学习分析与规划平台',
    'nav.demo': '演示',
    'nav.about': '关于我们',
    'nav.contact': '联系方式',
    'dashboard.title': '学生发展规划',
    'dashboard.subtitle': '学生个人档案与分析',

    // 分类标题
    'category.all': '全部',
    'category.profile': '学生档案',
    'category.subjects': '学科分析',
    'category.language': '语言能力',
    'category.planning': '升学规划',

    // 菜单项
    'menu.student_overview': '学生概况',
    'menu.academic_overview': '学术概览',
    'menu.mathematics': '数学',
    'menu.biology': '生物',
    'menu.chemistry': '化学',
    'menu.english': '英语',
    'menu.chinese': '中文',
    'menu.ict': '信息技术',
    'menu.business': '商业',
    'menu.global': '全球视野',
    'menu.english_proficiency': '英语能力',
    'menu.ielts': '雅思分析',
    'menu.alevel': 'A-level规划',
    'menu.activity': '活动规划',
    'menu.action_plan_en': '行动计划(英)',
    'menu.action_plan_cn': '行动计划(中)',

    // 首页内容
    'home.welcome': '欢迎使用学生升学规划系统',
    'home.profile.title': '个人档案',
    'home.profile.desc': '查看学生的基本信息、学习风格、性格特点和职业兴趣。',
    'home.subjects.title': '学科分析',
    'home.subjects.desc': '了解各学科的表现、优势和提升空间，为A-level选科提供依据。',
    'home.language.title': '语言能力',
    'home.language.desc': '评估英语水平和雅思备考进度，为海外留学做准备。',
    'home.planning.title': '升学规划',
    'home.planning.desc': '查看详细的A-level规划、活动安排和行动计划时间表。',
    'home.guide.title': '使用指南',
    'home.guide.desc': '本系统提供全面的学生学术分析和升学规划工具。使用左侧导航菜单，您可以：',
    'home.guide.item1': '查看学生的个人档案和学术表现',
    'home.guide.item2': '分析各学科的优势和提升空间',
    'home.guide.item3': '了解语言能力评估和提升计划',
    'home.guide.item4': '获取详细的升学规划和行动计划',
    'home.start': '点击左侧菜单项，开始探索详细的学生档案和升学规划',

    // 着陆页内容
    'landing.hero.title1': '驱动的新一代',
    'landing.hero.title2': '智能学习分析报告',
    'landing.hero.desc': '利用人工智能技术，为每位学生提供个性化的学习分析和发展规划，帮助学生发掘潜能，实现学术和职业目标。',
    'landing.cta.demo': '查看演示',
    'landing.cta.more': '了解更多',
    'landing.features.title': '智能分析',
    'landing.features.subtitle': '为教育决策提供数据支持',
    'landing.feature1.title': '数据驱动分析',
    'landing.feature1.desc': '基于学生的历史表现数据，生成全面的学术分析报告，识别优势和提升空间。',
    'landing.feature2.title': '个性化推荐',
    'landing.feature2.desc': '根据学生的学习风格、兴趣和目标，提供定制化的学习策略和发展建议。',
    'landing.feature3.title': '未来规划',
    'landing.feature3.desc': '结合学生的学术表现和职业兴趣，提供科学的升学规划和职业发展路径。',
    'landing.templates.title': '报告模板',
    'landing.templates.subtitle': '多样化的分析报告模板',
    'landing.templates.desc': '为不同的教育需求提供专业的分析报告，像乐高积木一样组合模块形成个性化报告',
    'landing.template.view': '查看示例',
    'landing.footer.rights': '保留所有权利。',

    // 报告模板
    'template.profile.title': '大学申请导向报告',
    'template.profile.desc': '以大学申请为目标的超详细分析报告，适合高中阶段学生',
    'template.profile.feature1': '学生综合概况',
    'template.profile.feature2': '学术竞争力分析',
    'template.profile.feature3': '申请策略规划',
    'template.profile.feature4': '个性化提升方案',
    'template.subjects.title': '日常学习指导报告',
    'template.subjects.desc': '关注日常学习进度和能力培养，提供实时学习反馈和指导',
    'template.subjects.feature1': '学科能力诊断',
    'template.subjects.feature2': '学习习惯分析',
    'template.subjects.feature3': '短期提升策略',
    'template.subjects.feature4': '个性化学习路径',
    'template.planning.title': '兴趣探索型报告',
    'template.planning.desc': '适合幼儿园和小学阶段，注重兴趣培养和多元能力发展',
    'template.planning.feature1': '兴趣倾向测评',
    'template.planning.feature2': '多元智能探索',
    'template.planning.feature3': '亲子活动建议',
    'template.planning.feature4': '成长里程规划',
  },
  en: {
    // Navigation and titles
    'app.title': 'AI Learning Analysis Report',
    'app.subtitle': 'Intelligent Learning Analysis & Planning Platform',
    'nav.demo': 'Demo',
    'nav.about': 'About Us',
    'nav.contact': 'Contact',
    'dashboard.title': 'SDP Dashboard',
    'dashboard.subtitle': 'Student Profile & Analysis',

    // Category titles
    'category.all': 'All',
    'category.profile': 'Student Profile',
    'category.subjects': 'Subject Analysis',
    'category.language': 'Language Proficiency',
    'category.planning': 'Academic Planning',

    // Menu items
    'menu.student_overview': 'Student Overview',
    'menu.academic_overview': 'Academic Overview',
    'menu.mathematics': 'Mathematics',
    'menu.biology': 'Biology',
    'menu.chemistry': 'Chemistry',
    'menu.english': 'English',
    'menu.chinese': 'Chinese',
    'menu.ict': 'ICT',
    'menu.business': 'Business',
    'menu.global': 'Global Perspectives',
    'menu.english_proficiency': 'English Proficiency',
    'menu.ielts': 'IELTS Analysis',
    'menu.alevel': 'A-Level Planning',
    'menu.activity': 'Activity Planning',
    'menu.action_plan_en': 'Action Plan (EN)',
    'menu.action_plan_cn': 'Action Plan (CN)',

    // Home page content
    'home.welcome': 'Welcome to Student Academic Planning System',
    'home.profile.title': 'Personal Profile',
    'home.profile.desc': 'View student information, learning style, personality traits and career interests.',
    'home.subjects.title': 'Subject Analysis',
    'home.subjects.desc': 'Understand performance, strengths and areas for improvement to guide A-level subject selection.',
    'home.language.title': 'Language Proficiency',
    'home.language.desc': 'Assess English level and IELTS preparation progress for overseas study.',
    'home.planning.title': 'Academic Planning',
    'home.planning.desc': 'View detailed A-level planning, activity arrangements and action plan timeline.',
    'home.guide.title': 'User Guide',
    'home.guide.desc': 'This system provides comprehensive student academic analysis and planning tools. Using the left navigation menu, you can:',
    'home.guide.item1': 'View student profiles and academic performance',
    'home.guide.item2': 'Analyze strengths and areas for improvement across subjects',
    'home.guide.item3': 'Understand language assessment and improvement plans',
    'home.guide.item4': 'Access detailed academic planning and action plans',
    'home.start': 'Click on the menu items on the left to explore detailed student profiles and academic planning',

    // Landing page content
    'landing.hero.title1': '-Powered Next Generation',
    'landing.hero.title2': 'Intelligent Learning Analysis Reports',
    'landing.hero.desc': 'Using artificial intelligence technology to provide personalized learning analysis and development planning for each student, helping them discover potential and achieve academic and career goals.',
    'landing.cta.demo': 'View Demo',
    'landing.cta.more': 'Learn More',
    'landing.features.title': 'Intelligent Analysis',
    'landing.features.subtitle': 'Data Support for Educational Decision-Making',
    'landing.feature1.title': 'Data-Driven Analysis',
    'landing.feature1.desc': 'Generate comprehensive academic analysis reports based on student performance data, identifying strengths and areas for improvement.',
    'landing.feature2.title': 'Personalized Recommendations',
    'landing.feature2.desc': 'Provide customized learning strategies and development suggestions based on student learning style, interests and goals.',
    'landing.feature3.title': 'Future Planning',
    'landing.feature3.desc': 'Provide scientific academic planning and career development paths based on student academic performance and career interests.',
    'landing.templates.title': 'Report Templates',
    'landing.templates.subtitle': 'Diverse Analysis Report Templates',
    'landing.templates.desc': 'Provide professional analysis reports for different educational needs, combining modules like Lego blocks to form personalized reports',
    'landing.template.view': 'View Example',
    'landing.footer.rights': 'All rights reserved.',

    // Report templates
    'template.profile.title': 'University Application-Oriented Report',
    'template.profile.desc': 'Detailed analysis report targeting university applications, suitable for high school students',
    'template.profile.feature1': 'Comprehensive Student Overview',
    'template.profile.feature2': 'Academic Competitiveness Analysis',
    'template.profile.feature3': 'Application Strategy Planning',
    'template.profile.feature4': 'Personalized Improvement Plan',
    'template.subjects.title': 'Daily Learning Guidance Report',
    'template.subjects.desc': 'Focuses on daily learning progress and skill development, providing real-time learning feedback and guidance',
    'template.subjects.feature1': 'Subject Capability Diagnosis',
    'template.subjects.feature2': 'Learning Habits Analysis',
    'template.subjects.feature3': 'Short-term Improvement Strategies',
    'template.subjects.feature4': 'Personalized Learning Path',
    'template.planning.title': 'Interest Exploration Report',
    'template.planning.desc': 'Suitable for kindergarten and early primary school stages, focusing on interest cultivation and diverse ability development',
    'template.planning.feature1': 'Interest Tendency Assessment',
    'template.planning.feature2': 'Multiple Intelligence Exploration',
    'template.planning.feature3': 'Parent-Child Activity Suggestions',
    'template.planning.feature4': 'Growth Milestone Planning',
  }
};

interface LanguageProviderProps {
  children: ReactNode;
}

// 语言提供者组件
export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Default to English
  const [language, setLanguage] = useState<Language>('en');

  // Translation function
  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const value = {
    language,
    setLanguage,
    t
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// 使用语言的自定义Hook
export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};